import { getRowValue, parseXer } from './parseXer'
import {
  ParsedXerSchedule,
  XER_TASK_DATE_FIELDS,
  XerProjectMeta,
  XerTask,
  XerTaskDates,
  XerWbsNode,
} from './xerTypes'

function emptyToNull(value: string): string | null {
  const trimmed = value.trim()
  return trimmed === '' ? null : trimmed
}

function buildWbsPath(
  wbsId: string,
  byId: Map<string, XerWbsNode>,
): string {
  const parts: string[] = []
  let current: string | null = wbsId
  const seen = new Set<string>()

  while (current && !seen.has(current)) {
    seen.add(current)
    const node = byId.get(current)
    if (!node) {
      break
    }
    parts.unshift(node.wbsName)
    current = node.parentWbsId
  }

  return parts.join(' > ')
}

function parseProjectMeta(
  tables: ReturnType<typeof parseXer>,
): XerProjectMeta | null {
  const projectTable = tables.PROJECT
  if (!projectTable || projectTable.rows.length === 0) {
    return null
  }

  const row = projectTable.rows[0]
  return {
    projId: getRowValue(projectTable, row, 'proj_id'),
    projShortName: getRowValue(projectTable, row, 'proj_short_name'),
    planStartDate: emptyToNull(
      getRowValue(projectTable, row, 'plan_start_date'),
    ),
    scdEndDate: emptyToNull(getRowValue(projectTable, row, 'scd_end_date')),
  }
}

function parseWbsNodes(
  tables: ReturnType<typeof parseXer>,
): XerWbsNode[] {
  const wbsTable = tables.PROJWBS
  if (!wbsTable) {
    return []
  }

  return wbsTable.rows.map(row => {
    const parentRaw = getRowValue(wbsTable, row, 'parent_wbs_id')
    return {
      wbsId: getRowValue(wbsTable, row, 'wbs_id'),
      parentWbsId: emptyToNull(parentRaw),
      wbsName: getRowValue(wbsTable, row, 'wbs_name'),
      wbsShortName: getRowValue(wbsTable, row, 'wbs_short_name'),
    }
  })
}

function parseTaskDates(
  table: NonNullable<ReturnType<typeof parseXer>['TASK']>,
  row: string[],
): XerTaskDates {
  const dates = {} as XerTaskDates
  for (const field of XER_TASK_DATE_FIELDS) {
    dates[field] = emptyToNull(getRowValue(table, row, field))
  }
  return dates
}

function parseTasks(
  tables: ReturnType<typeof parseXer>,
  wbsById: Map<string, XerWbsNode>,
): XerTask[] {
  const taskTable = tables.TASK
  if (!taskTable) {
    return []
  }

  return taskTable.rows.map(row => {
    const wbsId = getRowValue(taskTable, row, 'wbs_id')
    return {
      taskId: getRowValue(taskTable, row, 'task_id'),
      projId: getRowValue(taskTable, row, 'proj_id'),
      wbsId,
      taskCode: getRowValue(taskTable, row, 'task_code'),
      taskName: getRowValue(taskTable, row, 'task_name'),
      taskType: getRowValue(taskTable, row, 'task_type'),
      statusCode: getRowValue(taskTable, row, 'status_code'),
      guid: getRowValue(taskTable, row, 'guid'),
      wbsPath: buildWbsPath(wbsId, wbsById),
      dates: parseTaskDates(taskTable, row),
    }
  })
}

/**
 * Parse a .xer file into schedule domain objects used by the import UI.
 */
export function parseXerSchedule(content: string): ParsedXerSchedule {
  const tables = parseXer(content)
  const wbs = parseWbsNodes(tables)
  const wbsById = new Map(wbs.map(node => [node.wbsId, node]))

  return {
    project: parseProjectMeta(tables),
    wbs,
    tasks: parseTasks(tables, wbsById),
  }
}
